/* ═══════════════════════════════════════════════════════════════════════
   Explorer multi-select semantics — the ONE implementation.

   Josh's standing law: selection behaves like Windows Explorer, everywhere.
   Plain click replaces, Ctrl toggles, Shift ranges from the anchor,
   Ctrl+Shift adds a range. Pure functions, so the rules are provable
   without a DOM and every surface can stand on the same proof.

   Ranges run in LIST order — the stable order the surface renders from
   (flagged-item order in the dirty reader, zone-token order on the forma
   canvas, document order in the Spec Studio) — never in queried-DOM order.
   The two usually agree; when a future surface renders out of order, the
   list is the truth.

   Extracted from `components/dirtyReaderSelection.ts` for the Explorer
   multi-select sweep (Phase 1 item 5, docs/MASTERS_UNIFIED_PLAN.md).
   ══════════════════════════════════════════════════════════════════════ */

export type SelectionMods = { ctrl: boolean; shift: boolean };

export type SelectionState = {
  /** Item indices, ascending, no duplicates. */
  selection: number[];
  /** Where the next Shift range starts from. */
  anchor: number;
};

const sorted = (xs: Iterable<number>) => Array.from(new Set(xs)).sort((a, b) => a - b);

function range(a: number, b: number): number[] {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const out: number[] = [];
  for (let i = lo; i <= hi; i++) out.push(i);
  return out;
}

/**
 * Explorer semantics for a click on list item `index`.
 *
 * - plain          → that item alone; it becomes the anchor
 * - Ctrl           → toggle it in/out; it becomes the anchor
 * - Shift          → contiguous range anchor…index, replacing the selection
 * - Ctrl+Shift     → that same range ADDED to the selection
 *
 * A Shift click with no anchor yet degrades to a plain click rather than
 * selecting from item 0 — clicking into a list should never sweep up
 * everything above the click.
 */
export function nextSelection(
  prev: number[],
  anchor: number | null,
  index: number,
  mods: SelectionMods
): SelectionState {
  if (mods.shift && anchor != null) {
    const span = range(anchor, index);
    return {
      selection: mods.ctrl ? sorted([...prev, ...span]) : span,
      // The anchor SURVIVES a shift-click, so dragging the range back and
      // forth keeps growing/shrinking from the same origin — as in Explorer.
      anchor,
    };
  }
  if (mods.ctrl) {
    const has = prev.includes(index);
    const selection = has ? prev.filter((i) => i !== index) : sorted([...prev, index]);
    return { selection, anchor: index };
  }
  return { selection: [index], anchor: index };
}

/**
 * The same rules expressed over opaque ITEM KEYS instead of indices, for
 * surfaces whose selection is a set of ids (canvas token keys, block ids).
 * `list` is the stable order a Shift range runs in; keys outside it are left
 * exactly as they are, so a range inside one zone never disturbs another.
 *
 * A plain or Shift-only click REPLACES the whole selection (Explorer), so the
 * untouched keys drop; Ctrl and Ctrl+Shift keep them.
 */
export function nextSelectionByKey(
  prev: string[],
  anchorKey: string | null,
  key: string,
  list: string[],
  mods: SelectionMods
): { selection: string[]; anchor: string | null } {
  const index = list.indexOf(key);
  if (index < 0) return { selection: [key], anchor: key };

  const inList = new Set(list);
  const outside = prev.filter((k) => !inList.has(k));
  const prevIdx = prev.filter((k) => inList.has(k)).map((k) => list.indexOf(k));
  const anchorIdx = anchorKey != null && inList.has(anchorKey) ? list.indexOf(anchorKey) : null;

  const next = nextSelection(prevIdx, anchorIdx, index, mods);
  const picked = next.selection.map((i) => list[i]);
  // Ctrl (with or without Shift) is the only additive gesture: it keeps what
  // lives outside this list. Plain and Shift-alone replace everything.
  const selection = mods.ctrl ? [...outside, ...picked] : picked;
  return { selection, anchor: list[next.anchor] ?? null };
}

/**
 * The rows a "Select all" is allowed to touch: the ones the surface is
 * actually SHOWING. Tables filter internally (search box, column filters), so
 * the page's own list still holds rows the user cannot see — selecting those
 * is how "Select all" on a one-row search ends up offering "Delete 94".
 *
 * `visible` is null/undefined only until the table has reported (not mounted,
 * still loading); the page's full list stands in for that first frame alone.
 */
export function bulkSelectionSource<T>(visible: T[] | null | undefined, all: T[]): T[] {
  return visible ?? all;
}

/**
 * True when every row on screen is already selected. An empty screen is
 * false, so the toggle never reads "Deselect all" with nothing to deselect.
 * Selected ids that are currently filtered out don't count either way.
 */
export function allRowsSelected(visibleIds: string[], selected: ReadonlySet<string>): boolean {
  return visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
}
